import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { PassUploadJobsService, PassUploadJobResponse, PassUploadJobsResponse, PassUploadErrorFileResponse } from '@app/shared/services/pass-upload-jobs.service'
import { PassUploadJob, PASS_UPLOAD_TERMINAL_STATUSES } from '@app/models/pass-upload-job.model'
import { base64ToArrayBuffer } from '@app/utils/download'
import download from '@app/utils/download'
import { AuthService } from '@app/core/auth/services/auth.service'

/**
 * How far back finished runs stay listed — in the bell AND in the Service Utilities runs
 * panel (YB-2120). One constant on purpose: the two must agree about what still exists.
 */
export const UPLOAD_TASKS_RETENTION_DAYS = 7

/**
 * Background upload-tasks tracker behind the navbar bell (YB-1669).
 *
 * Staged pass uploads are queued by PAPI immediately after validation — this service
 * follows their progress GCP-style: save flows call PassUploadJobsService.trackJob()
 * with the job id from the event mutation response. Jobs are polled while any is
 * active. Newly tracked jobs with invalid rows additionally raise `errorJob$`, which
 * the app-root errors dialog listens on.
 *
 * The list is NOT session state: init() restores it from the API on every app load,
 * including recently FINISHED jobs, so a user who closed the browser or logged out
 * mid-upload still finds the outcome — and the error file — waiting for them. A job
 * leaves the bell only when the user dismisses it, which is persisted server-side
 * (`dismissed_at`); a local-only dismissal would resurrect on the next load. Finished
 * jobs older than RETENTION_DAYS drop off on their own so the bell can't grow forever.
 */
@Injectable({ providedIn: 'root' })
export class UploadTasksService {
  private jobsSubject = new BehaviorSubject<PassUploadJob[]>([])
  /** Tracked jobs, newest first (active + terminal-until-dismissed). */
  jobs$ = this.jobsSubject.asObservable()
  activeCount$: Observable<number> = this.jobs$.pipe(map((jobs) => jobs.filter((job) => this.isActive(job)).length))

  private errorJobSubject = new Subject<PassUploadJob>()
  /** A freshly tracked job that has invalid rows — the errors dialog host listens here. */
  errorJob$ = this.errorJobSubject.asObservable()

  private pollSubscription: Subscription | null = null
  /**
   * Who the list was restored for (AuthService.getIdentityKey(); `undefined` = not yet).
   * YB-2167: logout/login are SPA navigations that keep this root service alive, so a
   * plain "initialized" flag served client A's jobs in client B's bell after a
   * disconnect/connect in the same tab. Every entry point compares this at the point of
   * use — no auth-event subscription, so a login path that sets the token without
   * emitting can't slip past it (same pattern as YB-1999).
   */
  private loadedFor: string | null | undefined = undefined
  private readonly POLL_INTERVAL_MS = 4000
  private readonly RESTORE_LIMIT = 20

  constructor(
    private passUploadJobs: PassUploadJobsService,
    private authService: AuthService,
  ) {
    this.passUploadJobs.trackRequests$.subscribe((jobId) => this.trackNewJob(jobId))
  }

  /**
   * Restore the bell after a page load — running jobs AND recently finished ones, so a
   * result is never lost to a browser close/logout. PAPI already excludes jobs the user
   * dismissed. Idempotent per identity; called from the navbar. A different identity
   * (another client or user, or logged out) drops the previous list before anything is
   * fetched, so the bell never renders the last session's jobs.
   */
  init() {
    const identity = this.authService.getIdentityKey()
    if (identity === this.loadedFor) return
    this.reset()
    this.loadedFor = identity
    if (!identity) return
    this.passUploadJobs
      .getJobs(['queued', 'processing', 'completed', 'completed_with_errors', 'failed'], this.RESTORE_LIMIT)
      .pipe(take(1))
      .subscribe((response: PassUploadJobsResponse) => {
        if (!this.isCurrent(identity)) return
        if (!response?.success || !response.data?.length) return
        for (const raw of response.data) {
          const job = this.passUploadJobs.asJob(raw)
          if (this.isActive(job) || this.isRecent(job)) this.upsertJob(job)
        }
        this.ensurePolling()
      })
  }

  isActive(job: PassUploadJob): boolean {
    return !PASS_UPLOAD_TERMINAL_STATUSES.includes(job.status)
  }

  /** Re-open the errors dialog for a job that already finished (its error file outlives the run). */
  openErrors(job: PassUploadJob) {
    this.errorJobSubject.next(job)
  }

  /**
   * Acknowledge a finished job: gone from the bell here and on every other device.
   * Removed locally right away — a failed call would only mean it reappears next load.
   */
  dismiss(jobId: number) {
    this.jobsSubject.next(this.jobsSubject.value.filter((job) => +job.id !== +jobId))
    this.passUploadJobs
      .dismissJob(jobId)
      .pipe(take(1))
      .subscribe({ error: () => undefined })
  }

  /** Finished within UPLOAD_TASKS_RETENTION_DAYS. Unparseable/missing timestamps count as recent — better shown than silently dropped. */
  isRecent(job: PassUploadJob): boolean {
    const finishedAt = new Date(job.updated_at || job.created_at).getTime()
    if (!Number.isFinite(finishedAt)) return true
    return Date.now() - finishedAt <= UPLOAD_TASKS_RETENTION_DAYS * 24 * 60 * 60 * 1000
  }

  /**
   * Take a still-queued run off the queue. PAPI only allows it before the drain picks the
   * run up — a run already processing has committed rows and cannot be half-undone — so a
   * refusal comes back as `success: false` rather than as a canceled job.
   */
  cancel(job: PassUploadJob): Observable<boolean> {
    const identity = this.loadedFor
    return this.passUploadJobs.cancelJob(+job.id).pipe(
      map((response) => {
        if (response?.success && this.isCurrent(identity)) this.upsertJob(Object.assign(new PassUploadJob(), job, { status: 'canceled' }))
        return !!response?.success
      }),
    )
  }

  downloadErrorFile(jobId: number) {
    this.passUploadJobs
      .getErrorFile(jobId)
      .pipe(take(1))
      .subscribe((response: PassUploadErrorFileResponse) => {
        if (!response?.success || !response.file) return
        const blob = new Blob([base64ToArrayBuffer(response.file)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        download(blob, response.filename || `pass-upload-${jobId}-errors.xlsx`)
      })
  }

  private trackNewJob(jobId: number) {
    // A save under a new identity must not land on top of the previous session's list.
    this.init()
    const identity = this.loadedFor
    this.passUploadJobs
      .getJob(jobId)
      .pipe(take(1))
      .subscribe((response: PassUploadJobResponse) => {
        if (!this.isCurrent(identity)) return
        if (!response?.success || !response.data) return
        const job = this.passUploadJobs.asJob(response.data)
        this.upsertJob(job)
        this.ensurePolling()
        /* Only an excel upload's invalid rows are news at this moment — its validation just
           ran. A Service Utilities run's invalid rows are the numbers the user reviewed and
           confirmed a step earlier; popping them up again is noise. They stay one click away
           in the bell and the runs panel. */
        if (job.invalid_rows > 0 && (job.job_type ?? 'pass_upload') === 'pass_upload') this.errorJobSubject.next(job)
      })
  }

  private upsertJob(job: PassUploadJob) {
    const jobs = [...this.jobsSubject.value]
    const index = jobs.findIndex((existing) => +existing.id === +job.id)
    if (index === -1) jobs.unshift(job)
    else jobs[index] = job
    this.jobsSubject.next(jobs)
  }

  private ensurePolling() {
    if (this.pollSubscription) return
    this.pollSubscription = interval(this.POLL_INTERVAL_MS).subscribe(() => this.refreshActiveJobs())
  }

  private refreshActiveJobs() {
    // Identity changed under a running poll (logout, or another client): drop the old list
    // and stop polling it. init() restores the new identity's jobs, or leaves the bell empty.
    if (!this.isCurrent(this.loadedFor)) {
      this.init()
      return
    }
    const identity = this.loadedFor
    const active = this.jobsSubject.value.filter((job) => this.isActive(job))
    if (!active.length) {
      this.stopPolling()
      return
    }
    for (const job of active) {
      this.passUploadJobs
        .getJob(+job.id)
        .pipe(take(1))
        .subscribe((response: PassUploadJobResponse) => {
          if (!this.isCurrent(identity)) return
          if (response?.success && response.data) this.upsertJob(this.passUploadJobs.asJob(response.data))
        })
    }
  }

  /** True while the session is still the one a request was sent under — a reply that lands after a switch is dropped. */
  private isCurrent(identity: string | null | undefined): boolean {
    return identity !== undefined && identity === this.authService.getIdentityKey()
  }

  private reset() {
    this.stopPolling()
    if (this.jobsSubject.value.length) this.jobsSubject.next([])
  }

  private stopPolling() {
    this.pollSubscription?.unsubscribe()
    this.pollSubscription = null
  }
}
